import { useCallback, useEffect, useState } from "react";

import { useConstants } from "shared/hooks/useConstants";
import { useLocale } from "shared/hooks/useLocale";
import { useOrganizationBranchDetails } from "shared/hooks/useOrganizationBranchDetails";
import { useTariffs } from "shared/hooks/useTariffs";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { formatDateTime } from "shared/lib/dateTime";
import { tariffStatusOf } from "shared/lib/tariffStatus";
import { digitsOnly } from "shared/lib/formInput";
import { downloadFileFromUrl } from "shared/lib/downloadFile";
import { updateLegalData } from "services/organizationBranch/rest";
import { createServiceOrder, getCheckoutSettings } from "services/serviceOrders/rest";
import { PriceDto, TariffDto } from "services/tariffs/restModels";
import { TariffPeriod, TariffVariant } from "modules/settings/models";
import { useNotificationSnackbar } from "shared/ui";

import { openCloudPaymentsWidget } from "features/Tariff/lib/cloudPayments";

import {
    BANK_ACCOUNT_LENGTH,
    BIK_LENGTH,
    KPP_LENGTH,
    OGRNIP_LENGTH,
    OGRN_LENGTH,
} from "features/Tariff/constants";
import { useActiveTab } from "features/Tariff/hooks/useActiveTab";
import {
    innLength,
    isLegalDataFormValid,
    legalDataFieldError,
    legalDataToForm,
} from "features/Tariff/lib/legalDataForm";
import { LegalDataFormField, LegalDataFormValues, TariffState } from "features/Tariff/types";

// Цифровые поля реквизитов режем по длине прямо при вводе — лишние символы всё равно отказные.
const DIGIT_FIELD_LENGTHS: Partial<Record<LegalDataFormField, number>> = {
    kpp: KPP_LENGTH,
    ogrn: OGRN_LENGTH,
    ogrnip: OGRNIP_LENGTH,
    bankBik: BIK_LENGTH,
    bankCurrentAccountNumber: BANK_ACCOUNT_LENGTH,
    bankCorrespondentAccountNumber: BANK_ACCOUNT_LENGTH,
};

export const useTariffState = (): TariffState => {
    const locale = useLocale();

    const notify = useNotificationSnackbar();
    const [activeTab, setActiveTab] = useActiveTab();

    const { branch, loading, error, setBranch, refetch: refetchBranch } =
        useOrganizationBranchDetails();
    const { tariffs, loading: tariffsLoading, error: tariffsError } = useTariffs();
    const { constants, loading: constantsLoading, error: constantsError } = useConstants();

    const [form, setForm] = useState<LegalDataFormValues>(legalDataToForm(null));
    const [saving, setSaving] = useState<boolean>(false);

    const [paymentTariff, setPaymentTariff] = useState<TariffDto | null>(null);
    const [paymentPeriodId, setPaymentPeriodId] = useState<string>("Year");
    const [isOfferAccepted, setOfferAccepted] = useState<boolean>(false);
    const [paying, setPaying] = useState<boolean>(false);

    // Форма инициализируется из ответа REST — пока он не пришёл, полей ещё нет.
    useEffect(() => {
        if (branch) setForm(legalDataToForm(branch.legalData));
    }, [branch]);

    const tariff = branch ? branch.tariff : null;

    const tariffStatus = tariff ? tariffStatusOf(tariff, Date.now()) : ("expired" as const);

    const tariffStatusText = (() => {
        if (!tariff) return "";

        if (tariffStatus === "active") {
            return `Действует до ${formatDateTime(tariff.nextPaymentDate)}`;
        }

        if (tariffStatus === "overdue") {
            return `Просрочен, меню активны до ${formatDateTime(tariff.menusActiveTillDate)}`;
        }

        return `Истёк ${formatDateTime(tariff.nextPaymentDate)}`;
    })();

    const setFormField = useCallback((field: LegalDataFormField, value: string) => {
        setForm((current) => {
            if (field === "type") {
                // Смена формы организации обнуляет поля, которых у неё нет.
                return {
                    ...current,
                    type: value as LegalDataFormValues["type"],
                    inn: "",
                    kpp: "",
                    ogrn: "",
                    ogrnip: "",
                };
            }

            const maxLength =
                field === "inn" ? innLength(current.type) : DIGIT_FIELD_LENGTHS[field];

            return {
                ...current,
                [field]: maxLength ? digitsOnly(value, maxLength) : value,
            };
        });
    }, []);

    const fieldError = useCallback(
        (field: LegalDataFormField) => legalDataFieldError(form, field),
        [form]
    );

    const saveLegalData = useCallback(async () => {
        if (!isLegalDataFormValid(form)) return;

        setSaving(true);
        try {
            const isCompany = form.type === "RussianOOOCard";

            const updated = await updateLegalData({
                type: form.type,
                title: form.title.trim(),
                inn: form.inn,
                kpp: isCompany ? form.kpp : null,
                ogrn: isCompany ? form.ogrn : null,
                ogrnip: isCompany ? null : form.ogrnip,
                bankTitle: form.bankTitle.trim(),
                bankBik: form.bankBik,
                bankCurrentAccountNumber: form.bankCurrentAccountNumber,
                bankCorrespondentAccountNumber: form.bankCorrespondentAccountNumber,
            });

            if (updated && branch) setBranch({ ...branch, legalData: updated });

            notify.showSuccess("Реквизиты сохранены");
        } catch (e) {
            notify.showError("Не удалось сохранить реквизиты", e);
        } finally {
            setSaving(false);
        }
    }, [branch, form, notify, setBranch]);

    const formatPrice = useCallback(
        (price: PriceDto) => {
            const currency = (constants?.currencies || []).find(
                (item) => item.id === price.currencyId
            ) || null;

            return createCurrencyFormatter(currency, locale).toShow(price.price);
        },
        [constants, locale]
    );

    const openPayment = useCallback((nextTariff: TariffDto, periodId: string) => {
        setPaymentTariff(nextTariff);
        setPaymentPeriodId(periodId);
        setOfferAccepted(false);
    }, []);

    const closePayment = useCallback(() => setPaymentTariff(null), []);

    const orderPayload = useCallback(
        (type: "Card" | "Invoice") => ({
            type,
            tariff: paymentTariff!.id as TariffVariant,
            period: paymentPeriodId as TariffPeriod,
        }),
        [paymentPeriodId, paymentTariff]
    );

    const downloadInvoice = useCallback(async () => {
        if (!paymentTariff) return;

        setPaying(true);
        try {
            const order = await createServiceOrder(orderPayload("Invoice"));

            if (!order || order.type !== "Invoice" || !order.invoiceLink) {
                notify.showError("Счёт создан, но ссылка на файл не пришла");
                return;
            }

            await downloadFileFromUrl(order.invoiceLink, `Счёт-${order.invoiceId}.pdf`);
            closePayment();
        } catch (e) {
            notify.showError("Не удалось выставить счёт", e);
        } finally {
            setPaying(false);
        }
    }, [closePayment, notify, orderPayload, paymentTariff]);

    const payByCard = useCallback(async () => {
        if (!paymentTariff) return;

        setPaying(true);
        try {
            const checkout = await getCheckoutSettings();

            if (!checkout) {
                notify.showError("Оплата картой временно недоступна");
                return;
            }

            const order = await createServiceOrder(orderPayload("Card"));

            if (!order || order.type !== "Card") {
                notify.showError("Не удалось создать заказ");
                return;
            }

            const opened = openCloudPaymentsWidget(
                {
                    publicId: checkout.cloudPaymentsPublicId,
                    description: `Заказ №S${order.orderId}`,
                    amount: order.price.price / 100,
                    currency: order.price.currencyId,
                    invoiceId: `S${order.orderId}`,
                    skin: "mini",
                },
                {
                    onSuccess: () => notify.showSuccess("Оплата прошла успешно"),
                    onFail: () => notify.showError("Оплата не прошла"),
                    // Тариф продлевает вебхук от эквайринга — перечитываем заведение с задержкой.
                    onComplete: () => setTimeout(refetchBranch, 2000),
                }
            );

            if (!opened) {
                notify.showError("Не удалось открыть форму оплаты, обновите страницу");
                return;
            }

            closePayment();
        } catch (e) {
            notify.showError("Не удалось оплатить картой", e);
        } finally {
            setPaying(false);
        }
    }, [closePayment, notify, orderPayload, paymentTariff, refetchBranch]);

    return {
        tariff,
        legalData: branch ? branch.legalData : null,
        loading: loading || tariffsLoading || constantsLoading,
        error: error || tariffsError || constantsError,

        activeTab,
        setActiveTab,

        tariffStatus,
        tariffStatusText,

        form,
        setFormField,
        fieldError,
        isFormValid: isLegalDataFormValid(form),
        saving,
        saveLegalData,

        tariffs,
        formatPrice,

        paymentTariff,
        paymentPeriodId,
        isOfferAccepted,
        paying,
        openPayment,
        closePayment,
        setPaymentPeriodId,
        setOfferAccepted,
        payByCard,
        downloadInvoice,

        notify,
    };
};
